import { useMemo, useState } from "react";
import { AcceptJobDialog } from "@/components/shared/accept-job-dialog";
import { Link } from "react-router";
import MainLayout from "@/components/layouts";
import SimpleTable, { ColumnDef } from "@/components/shared/simple-table";
import { FilterItem } from "@/interfaces";
import { useIndividualDailyWork } from "./daily-work.hooks";
import { DailyWorkDayJoin } from "@/repositories/daily-work.repository";
import { format, parseISO } from "date-fns";
import { DateRange } from "react-day-picker";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Play, Check, Eye, ArrowRight, Loader2 } from "lucide-react";
import { daily_work_day_status } from "@/models/daily-work.model";
import { acceptDailyWorkDay } from "./daily-work.api";
import { fetchIndividualDailyWorkDays } from "./daily-work.slice";
import { toast } from "sonner";
import { WORK_DAY_SEARCH_MAX_LENGTH } from "@shyne/shared/api";

const statusBadgeConfig: Record<daily_work_day_status, { className: string; label: string }> = {
  [daily_work_day_status.pending]: { className: "bg-gray-100 text-gray-700", label: "Pending" },
  [daily_work_day_status.assigned]: { className: "bg-blue-100 text-blue-700", label: "Assigned" },
  [daily_work_day_status.accepted]: { className: "bg-yellow-100 text-yellow-700", label: "Accepted" },
  [daily_work_day_status.inprogress]: { className: "bg-orange-100 text-orange-700", label: "In Progress" },
  [daily_work_day_status.completed]: { className: "bg-green-100 text-green-700", label: "Completed" },
};

export default function IndividualDailyWorkPage() {
  const { list, total, filters, pagination, dispatch } = useIndividualDailyWork();
  const [acceptingId, setAcceptingId] = useState<string | null>(null);
  const [pendingAcceptId, setPendingAcceptId] = useState<string | null>(null);

  const handleAccept = async (dayId: string) => {
    setAcceptingId(dayId);
    try {
      await acceptDailyWorkDay(dayId);
      toast.success("Work accepted");
      dispatch(fetchIndividualDailyWorkDays());
    } catch (error: any) {
      toast.error(error.message || "Failed to accept");
    } finally {
      setAcceptingId(null);
    }
  };

  const filterItems: FilterItem[] = useMemo(
    () => [
      {
        type: "search",
        title: "Search Property",
        value: filters.search,
        onValueChange: filters.setSearch,
        maxLength: WORK_DAY_SEARCH_MAX_LENGTH,
        grid: 3,
      },
      {
        type: "dateRange",
        title: "Date Range",
        value: {
          from: filters.startDate ? parseISO(filters.startDate) : undefined,
          to: filters.endDate ? parseISO(filters.endDate) : undefined,
        } as DateRange,
        onValueChange: (range: DateRange | undefined) => {
          filters.setStartDate(range?.from ? format(range.from, "yyyy-MM-dd") : "");
          filters.setEndDate(range?.to ? format(range.to, "yyyy-MM-dd") : "");
        },
        grid: 4,
      },
      {
        type: "select",
        title: "Status",
        value: filters.status,
        onValueChange: (v: string) => filters.setStatus(v === "all" ? "" : v),
        options: [
          { label: "Assigned", value: "assigned" },
          { label: "Accepted", value: "accepted" },
          { label: "In Progress", value: "inprogress" },
          { label: "Completed", value: "completed" },
        ],
        grid: 2,
      },
    ],
    [filters.search, filters.startDate, filters.endDate, filters.status],
  );

  const cols = useMemo<ColumnDef<DailyWorkDayJoin>[]>(
    () => [
      {
        header: "Property",
        accessor: (r) => (
          <Link to={`/daily-work/${r.id}/execution?readonly=true`} className="flex flex-col hover:underline">
            <span className="font-medium">{r.daily_work_data.property.name}</span>
            {r.daily_work_data.property.address && (
              <span className="text-xs text-muted-foreground">{r.daily_work_data.property.address}</span>
            )}
          </Link>
        ),
        sortable: true,
        sortKey: "daily_work_data.property.name",
      },
      {
        header: "Date",
        accessor: (r) => format(parseISO(r.work_date), "MMM d, yyyy"),
        sortable: true,
        sortKey: "work_date",
      },
      {
        header: "Service",
        accessor: (r) => (
          <>
            <div className="font-medium">{r.service.name}</div>
            {r.service.parent_name && <div className="text-sm text-muted-foreground">{r.service.parent_name}</div>}
          </>
        ),
      },
      {
        header: "Tasks",
        accessor: (r) => (
          <div className="flex flex-col gap-1">
            {r.tasks.slice(0, 2).map((task, idx) => (
              <span key={idx} className="text-sm">
                • {task}
              </span>
            ))}
            {r.tasks.length > 2 && <span className="text-xs text-muted-foreground">+{r.tasks.length - 2} more</span>}
          </div>
        ),
      },
      {
        header: "Status",
        accessor: (r) => {
          const config = statusBadgeConfig[r.status] ?? statusBadgeConfig[daily_work_day_status.pending];
          return (
            <Badge variant="secondary" className={config.className}>
              {config.label}
            </Badge>
          );
        },
      },
      {
        header: "Payment",
        accessor: (r) =>
          r.individual_payment ? (
            <span className="font-medium text-green-600">${r.individual_payment.toFixed(2)}</span>
          ) : (
            <span className="text-muted-foreground">—</span>
          ),
      },
      {
        header: "Vendor",
        accessor: (r) => r.daily_work_data.vendor.display_name,
      },
      {
        header: "Actions",
        accessor: (row) => {
          if (row.status === daily_work_day_status.assigned) {
            return (
              <Button
                variant="default"
                size="sm"
                disabled={acceptingId === row.id}
                onClick={() => setPendingAcceptId(row.id)}
              >
                {acceptingId === row.id ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Check className="w-4 h-4 mr-2" />
                )}
                Accept
              </Button>
            );
          }
          if (row.status === daily_work_day_status.accepted) {
            return (
              <Link to={`/daily-work/${row.id}/execution`}>
                <Button variant="default" size="sm">
                  <Play className="w-4 h-4 mr-2" />
                  Go On-site
                </Button>
              </Link>
            );
          }
          if (row.status === daily_work_day_status.inprogress) {
            return (
              <Link to={`/daily-work/${row.id}/execution`}>
                <Button variant="outline" size="sm">
                  <ArrowRight className="w-4 h-4 mr-2" />
                  Continue
                </Button>
              </Link>
            );
          }
          if (row.status === daily_work_day_status.completed) {
            return (
              <Link to={`/daily-work/${row.id}/execution?readonly=true`}>
                <Button variant="ghost" size="sm">
                  <Eye className="w-4 h-4 mr-2" />
                  View
                </Button>
              </Link>
            );
          }
          return null;
        },
        className: "whitespace-nowrap",
      },
    ],
    [acceptingId],
  );

  return (
    <MainLayout
      breadcrumb={[{ name: "Daily Work", href: "#" }]}
      pageHeader={{
        title: "Daily Work",
        subtitle: "View and start your assigned daily work",
      }}
    >
      <SimpleTable<DailyWorkDayJoin>
        data={list}
        totalCount={total}
        columns={cols}
        page={pagination.page}
        pageSize={pagination.pageSize}
        setPage={pagination.setPage}
        setPageSize={pagination.setPageSize}
        sortKey={filters.sortKey}
        sortOrder={filters.sortOrder}
        onSortChange={filters.setSortKey}
        filters={filterItems}
        onReset={() => {
          filters.setSearch("");
          filters.setStartDate("");
          filters.setEndDate("");
          filters.setStatus("");
        }}
        onApply={() => {}}
      />
      <AcceptJobDialog
        open={!!pendingAcceptId}
        onOpenChange={(open) => {
          if (!open) setPendingAcceptId(null);
        }}
        onConfirm={() => {
          if (pendingAcceptId) handleAccept(pendingAcceptId);
          setPendingAcceptId(null);
        }}
        loading={!!acceptingId}
      />
    </MainLayout>
  );
}
